import { useState, useEffect } from 'react';
import { Schema, FormData, FormErrors } from '../types';

export function useFormFields(schema: Schema, initialValues: FormData | null) {
  const [formData, setFormData] = useState<FormData>(() => {
    const initial: FormData = {};
    schema.fields.forEach(f => {
      if (f.type === 'array') {
        initial[f.name] = (initialValues?.[f.name] as string[]) ?? [''];
      } else if (f.type === 'checkbox') {
        initial[f.name] = (initialValues?.[f.name] as boolean) ?? false;
      } else {
        initial[f.name] =
          (initialValues?.[f.name] as string) ?? f.defaultValue ?? '';
      }
    });
    return initial;
  });
  const [errors, setErrors] = useState<FormErrors>({});

  // Update form data when schema changes to ensure correct default values
  useEffect(() => {
    setFormData(prev => {
      const updated = { ...prev };
      schema.fields.forEach(f => {
        if (
          f.defaultValue !== undefined &&
          updated[f.name] !== f.defaultValue
        ) {
          updated[f.name] = f.defaultValue;
        }
      });
      return updated;
    });
  }, [schema]);

  const handleChange = (name: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleArrayChange = (name: string, idx: number, value: string) => {
    setFormData(prev => {
      const arr = [...(prev[name] as string[])];
      arr[idx] = value;
      return { ...prev, [name]: arr };
    });
  };

  const addArrayItem = (name: string) => {
    setFormData(prev => ({
      ...prev,
      [name]: [...(prev[name] as string[]), ''],
    }));
  };

  const removeArrayItem = (name: string, idx: number) => {
    setFormData(prev => {
      const arr = [...(prev[name] as string[])];
      arr.splice(idx, 1);
      return { ...prev, [name]: arr };
    });
  };

  return {
    formData,
    errors,
    setErrors,
    handleChange,
    handleArrayChange,
    addArrayItem,
    removeArrayItem,
  };
}
